import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { formFieldSx, FieldLabel } from '../../../styles/formStyles';
import { primaryIconColor } from '../../../styles/iconStyles';
import { getLocationsForAdministrativeLevel } from '../countryManagement.service';
import { resultMessage } from '../../../../service/apiResult';

const emptyConfirmState = { open: false, level: null, locations: [], totalCount: 0 };

/**
 * `countryId` is only available once the country has been saved at least once (edit mode). A level
 * added in this same session and not yet saved (`level.id` is null) cannot have any location
 * recorded against it either, so both are skipped straight to a local removal — the confirmation
 * check only ever needs to run for a level that already exists in the database.
 */
function AdministrativeLocationTypesManager({ levels, onChange, readOnly = false, countryId = null, onCascadeConfirm, onError }) {
  const [editingId, setEditingId] = useState(null);
  const [editingLevel, setEditingLevel] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLevelName, setNewLevelName] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmState, setConfirmState] = useState(emptyConfirmState);
  const [checkingLevelId, setCheckingLevelId] = useState(null);

  const handleAddLevel = () => {
    if (!newLevelName.trim()) {
      return;
    }

    const nextLevel = (levels.length > 0 ? Math.max(...levels.map(l => l.level || 0)) : 0) + 1;
    const newLevel = {
      id: null,
      level: nextLevel,
      name: newLevelName.trim(),
    };

    onChange([...levels, newLevel]);
    setNewLevelName('');
    setShowAddForm(false);
  };

  const handleEditClick = (level) => {
    setEditingId(level.id);
    setEditingLevel(level.level);
    setEditingName(level.name);
    setOpenDialog(true);
  };

  const handleSaveEdit = () => {
    if (!editingName.trim()) {
      return;
    }

    const updated = levels.map(l =>
      l.id === editingId || (editingId === null && l.level === editingLevel)
        ? { ...l, name: editingName.trim() }
        : l
    );

    onChange(updated);
    setOpenDialog(false);
    setEditingId(null);
    setEditingLevel(null);
    setEditingName('');
  };

  const removeLevel = (level) => {
    const updated = levels.filter(l => l.id !== level.id && l.level !== level.level);
    onChange(updated);
  };

  /**
   * A level added this session (no id yet) can't have any location recorded against it, so it is
   * always safe to remove locally without asking the backend. A saved level might — check first,
   * and only interrupt with the confirmation modal when it actually does.
   */
  const handleDeleteClick = async (level) => {
    if (!level.id || !countryId) {
      removeLevel(level);
      return;
    }

    setCheckingLevelId(level.id);
    const result = await getLocationsForAdministrativeLevel(countryId, level.id);
    setCheckingLevelId(null);

    if (!result.success) {
      onError?.(resultMessage(result, 'Could not check locations for this level. Try again.'));
      return;
    }

    const payload = result.data?.data ?? result.data ?? {};
    const locations = Array.isArray(payload.locations) ? payload.locations : [];
    const totalCount = typeof payload.totalCount === 'number' ? payload.totalCount : locations.length;

    if (totalCount === 0) {
      removeLevel(level);
      return;
    }

    setConfirmState({ open: true, level, locations, totalCount });
  };

  const handleCancelCascadeRemove = () => {
    setConfirmState(emptyConfirmState);
  };

  const handleConfirmCascadeRemove = () => {
    const { level } = confirmState;
    removeLevel(level);
    onCascadeConfirm?.(level.id);
    setConfirmState(emptyConfirmState);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setEditingLevel(null);
    setEditingName('');
  };

  if (levels.length === 0 && readOnly) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" sx={{ color: '#625B71' }}>
          No location types defined
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Levels List */}
      {levels.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {levels.map((level, index) => (
            <Paper
              key={level.id || level.level}
              sx={{
                p: 2,
                mb: 1.5,
                border: '1px solid #DFE5F6',
                backgroundColor: '#FFFFFF',
                boxShadow: '0px 2px 4px rgba(31, 46, 85, 0.04)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                '&:hover': {
                  boxShadow: '0px 4px 8px rgba(31, 46, 85, 0.08)',
                  transition: 'box-shadow 0.2s ease',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: '#DFE5F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    color: '#294190',
                    fontSize: '14px',
                  }}
                >
                  {level.level}
                </Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#1C1B1F',
                  }}
                >
                  {level.name}
                </Typography>
              </Box>

              {!readOnly && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditClick(level)}
                    sx={{
                      color: primaryIconColor,
                      '&:hover': { backgroundColor: '#F5F5F5' },
                    }}
                    title="Edit"
                  >
                    <Icon icon="mdi:pencil" width="20" height="20" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(level)}
                    disabled={checkingLevelId === level.id}
                    sx={{
                      color: '#C40000',
                      '&:hover': { backgroundColor: '#FEE2E2' },
                    }}
                    title="Delete"
                  >
                    {checkingLevelId === level.id
                      ? <CircularProgress size={18} color="inherit" />
                      : <Icon icon="mdi:trash-can" width="20" height="20" />}
                  </IconButton>
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {/* Add New Level Section */}
      {!readOnly && (
        <Box>
          {!showAddForm ? (
            <Button
              onClick={() => setShowAddForm(true)}
              startIcon={<Icon icon="mdi:plus" width="20" height="20" />}
              sx={{
                textTransform: 'none',
                color: '#294190',
                fontSize: '16px',
                fontWeight: 500,
                px: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              Add Level
            </Button>
          ) : (
            <Paper
              sx={{
                p: 2,
                border: '1px solid #DFE5F6',
                backgroundColor: '#FAFBFF',
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 48,
                    borderRadius: '4px',
                    backgroundColor: '#DFE5F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    color: '#294190',
                    fontSize: '14px',
                    flexShrink: 0,
                  }}
                >
                  {levels.length + 1}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Enter level name (e.g., State, District)"
                    value={newLevelName}
                    onChange={(e) => setNewLevelName(e.target.value)}
                    sx={formFieldSx}
                    autoFocus
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewLevelName('');
                  }}
                  sx={{
                    textTransform: 'none',
                    color: '#294190',
                    fontSize: '16px',
                    fontWeight: 500,
                    height: 40,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddLevel}
                  sx={{
                    height: 40,
                    borderRadius: '10px',
                    backgroundColor: '#294190',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#1F3876',
                    },
                  }}
                >
                  Add
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#1C1B1F',
          }}
        >
          Edit Level {editingLevel}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            placeholder="Enter level name"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            sx={formFieldSx}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: 'none',
              color: '#294190',
              fontSize: '16px',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={{
              borderRadius: '10px',
              backgroundColor: '#294190',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#1F3876',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove-level confirmation: shown only when the level being removed still has locations
          recorded against it — a level with none is removed immediately, with no interruption. */}
      <Dialog open={confirmState.open} onClose={handleCancelCascadeRemove} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600, color: '#1C1B1F' }}>
          Remove "{confirmState.level?.name}"?
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: '#625B71', mb: 2 }}>
            {confirmState.totalCount} location{confirmState.totalCount === 1 ? '' : 's'} {confirmState.totalCount === 1 ? 'is' : 'are'} still
            recorded at this level. Removing it will deactivate {confirmState.totalCount === 1 ? 'this location' : 'all of them'} — and
            anything beneath {confirmState.totalCount === 1 ? 'it' : 'them'} — so the level can be safely removed. Deactivated
            locations stay on record and can be browsed and reactivated from Geographical Data &rarr; View &amp; Edit Locations.
          </Typography>

          <List
            dense
            sx={{
              maxHeight: 320,
              overflowY: 'auto',
              border: '1px solid #E7ECF2',
              borderRadius: 2,
              py: 0,
            }}
          >
            {confirmState.locations.map((loc) => (
              <ListItem key={loc.id} divider>
                <ListItemText
                  primary={loc.name}
                  secondary={[loc.code, loc.active === false ? 'already inactive' : null].filter(Boolean).join(' · ') || undefined}
                />
              </ListItem>
            ))}
          </List>
          {confirmState.totalCount > confirmState.locations.length && (
            <Typography variant="caption" sx={{ color: '#625B71', display: 'block', mt: 1 }}>
              And {confirmState.totalCount - confirmState.locations.length} more.
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: '#625B71', display: 'block', mt: 2 }}>
            Nothing is deactivated yet — this takes effect once you save the country.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCancelCascadeRemove}
            sx={{ textTransform: 'none', color: '#294190', fontSize: '16px', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCascadeRemove}
            variant="contained"
            sx={{
              borderRadius: '10px',
              backgroundColor: '#C40000',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#A30000' },
            }}
          >
            Deactivate &amp; Remove Level
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdministrativeLocationTypesManager;
